import { api, selfAppliedApi } from '@/api'
import { BaseApi } from '@/api/BaseApi.ts'
import type {
  CollectAuditData,
  CollectAuditFetchParams,
  CollectFetchData,
  CollectFormData,
  GetCollectSharedData,
} from '@/api/Collect/types.ts'
import type { FetchParams, Paginated, ParamType } from '@/api/types.ts'

class Api extends BaseApi<
  GetCollectSharedData,
  Paginated<CollectFetchData>,
  CollectFormData,
  CollectAuditFetchParams
> {
  readonly baseUrlWithoutView =
    '/initiatives/:initiativeId/features/:featureId/collects'

  readonly baseUrl =
    '/initiatives/:initiativeId/features/:featureId/collects/:viewMode'

  async create(
    params: ParamType[],
    payload: CollectFormData,
    publicAccess?: boolean
  ): Promise<unknown> {
    if (publicAccess) {
      const { data } = await selfAppliedApi.post<unknown>(
        'features-public/collects',
        payload,
        this.config
      )
      return data
    }
    const url = this.getBaseUrl(`${this.baseUrl}/:version`, params)
    const { data } = await api.post<unknown>(url, payload, this.config)
    return data
  }

  async update(
    id: ParamType | ParamType[],
    payload: CollectFormData
  ): Promise<unknown> {
    const params = this.toParamArray(id)
    const url = this.getBaseUrl(`${this.baseUrl}/:version/:id`, params)
    const { data } = await api.put<unknown>(url, payload, this.config)
    return data
  }

  async fetchAudits(
    id: ParamType | ParamType[],
    queryParams: FetchParams
  ): Promise<Paginated<CollectAuditData>> {
    const filteredParams: Record<string, string> =
      this.buildFetchParams(queryParams)

    const URParams = new URLSearchParams(filteredParams)
    const params = this.toParamArray(id)
    const url = this.getBaseUrl(
      `${this.baseUrl}/:id/audits?${URParams.toString()}`,
      params
    )
    const { data } = await api.get<Paginated<CollectAuditData>>(
      url,
      this.config
    )
    return data
  }

  async get(
    id: ParamType | ParamType[],
    version?: ParamType
  ): Promise<GetCollectSharedData> {
    const filteredParams: Record<string, string> = this.buildFetchParams({
      version,
    })

    const URParams = new URLSearchParams(filteredParams)
    const params = this.toParamArray(id)
    const url = this.getBaseUrl(`${this.baseUrl}/:id?${URParams.toString()}`, [
      ...params,
    ])
    const { data } = await api.get<GetCollectSharedData>(url, this.config)
    return data
  }

  override async fetch(
    params: ParamType[],
    queryParams: CollectAuditFetchParams = {} as CollectAuditFetchParams
  ): Promise<Paginated<CollectFetchData>> {
    const filteredParams: Record<string, string> =
      this.buildFetchParams(queryParams)

    const URParams = new URLSearchParams(filteredParams)
    const url = `${this.getBaseUrl(this.baseUrlWithoutView, params)}?${URParams.toString().replaceAll('+', '%20')}`
    const { data } = await api.get<Paginated<CollectFetchData>>(url, this.config)
    return data
  }
}

const CollectApi = new Api()

export default CollectApi
